<template>
    <div v-if="template"
         class="py-6 mt-16 xl:mt-0"
    >
        <interview-template-title :count-items="countItems"
                                  :value="template"
                                  :service="service"
                                  @edit="handleEdit(template)"
                                  @reset="$emit('reset')"
        />

        <div class="flex items-center justify-between mb-16">
            <h3 class="text-body font-semibold">{{ moduleInfo.text.sub_title }}</h3>

            <button
                class="bg-white inline-flex items-center pl-14 pr-12 py-8 rounded-6 border border-primary-20 text-primary hover:bg-primary-5"
                v-if="!hasAutocomplete"
                @click="createNewItem(null)">
                <svg-icon v-if="saving" class="animate-spin w-20 h-20 mr-8" size="small"
                          type="loader"/>
                <svg-icon v-if="!saving" class="inline max-w-16 max-h-16 text-secondary mr-8" type="add-circle"/>
                {{ moduleInfo.text.new_item }}
            </button>
        </div>

        <div class="rounded-4 border border-primary-5">
            <div v-if="hasAutocomplete && canManage"
                 class="flex items-center py-16 pl-20 pr-64 border-b-2 bg-primary-5">
                <div class="max-w-64 w-full inline-flex items-center pl-8">
                    <svg-icon v-if="creating" class="animate-spin w-20 h-20" size="small"
                              type="loader"/>
                </div>
                <select-dropdown
                    :static-id ="staticId + 'Dropdown'"
                    add-new
                    :allow-enter="true"
                    :allow-similar="true"
                    :auto-translate="false"
                    class="w-full pr-48"
                    enable-when-no-options
                    :options="options"
                    :placeholder="moduleInfo.text.new_item"
                    :ref="'item-new-' + newItems"
                    searchable
                    v-model="newItem"
                    @change="changeLinkedItem(null, $event)"
                />
            </div>
            <draggable
                v-bind="dragOptions"
                v-if="items.length > 0"
                v-model="items"
                handle=".draggable-handle"
                :item-key="item => getKey(item)"
                class="divide-y divide-primary-5"
                @end="updateOrder"
                @start="drag = true"
            >
                <template #item="{ element, index }">
                    <div
                         class="flex py-16 pr-32 relative group item"
                         :data-id="getKey(element)"
                         @mousedown="currentItem = element"
                         @mouseleave="actionButtons = null"
                         @mouseover="actionButtons = getUniqueId(element)"
                    >
                        <div class="draggable-item pl-20 w-full">
                            <div
                                class="transition-all duration-100 absolute inset-y-0 left-0 w-0 bg-secondary-80 group-hover:w-4"></div>

                            <div class="flex items-center mr-auto pr-64">
                                <strong class="max-w-64 w-full text-secondary-80 text-heading-1 whitespace-nowrap">
                                    {{ moduleInfo.text.item_short }} {{ index + 1 }}
                                </strong>

                                <svg-icon v-if="element.id === null" class="animate-spin w-16 h-16 mx-auto"
                                          size="small" type="loader"/>

                                <template v-if="hasAutocomplete && optionsLoaded">
                                    <select-dropdown
                                        v-model="element.id"
                                        :auto-translate="false"
                                        :ref="'item-' + getKey(element)"
                                        :options="options"
                                        :placeholder="$t(`settings.${moduleInfo.templateModel}.${moduleInfo.title.attribute}`)"
                                        add-new
                                        :allow-enter="true"
                                        class="flex items-center cursor-text w-full pr-16 min-h-32"
                                        @change="changeLinkedItem(element, $event)"
                                        searchable
                                    />
                                </template>
                            </div>
                        </div>

                        <div
                            v-show="actionButtons === getUniqueId(element) || deleting === getUniqueId(element)"
                            class="flex items-center absolute inset-y-0 right-0 pr-8"
                        >
                            <div class="draggable-handle cursor-move text-primary-80 mr-8" v-if="canManage">
                                <svg-icon class="max-h-16 max-w-16" type="drag"/>
                            </div>
                            <button
                                class="flex items-center justify-center text-primary-80 group-hover:text-primary h-32 w-32"
                                :title="$t('general.edit_subject',{subject: $t(`settings.${moduleInfo.model}.single`)})"
                                @click="handleEdit(element)"
                            >
                                <svg-icon v-if="saving" class="animate-spin w-16 h-16" size="small" type="loader"/>
                                <svg-icon v-else class="max-w-16 max-h-16" type="edit"/>
                            </button>

                            <button
                                v-if="canManage"
                                :class="!deleting ? 'hover:text-semantic-error' : ''"
                                class="w-32 h-32"
                                :title="$t('general.delete_subject',{subject: $t(`settings.${moduleInfo.model}.single`)})"
                                @click="deleteItem(element)">

                                <svg-icon v-if="deleting && deleting === getUniqueId(element)"
                                          class="animate-spin w-16 h-16 mx-auto" size="small" type="loader"/>
                                <svg-icon v-else class="max-w-16 max-h-16 mx-auto" type="delete"/>
                            </button>
                        </div>
                    </div>
                </template>
            </draggable>

            <p v-if="countItems === 0"
               class="p-14 text-center text-primary-80"
               v-html="moduleInfo.text.no_items_found">
            </p>

            <popup-modal-confirm
                v-if="showDeletePopup"
                :cancel-text="$t('settings.filter-groups.delete.group.message')"
                :confirm-text="$t('settings.filter-groups.delete.permanent.message')"
                :message="$t('settings.filter-groups.delete.filter.info')"
                show-exclamation
                :title="$t('settings.filter-groups.delete.filter.title')"
                @confirm="confirmPermanentDeletion"
                @close="confirmDeletion"
            />

            <div v-if="countItems && countItems > 0" class="flex justify-end p-16 border-t border-primary-5">
                <button
                    class="bg-white inline-flex items-center pl-14 pr-12 py-8 rounded border border-primary-20 text-primary hover:bg-primary-5"
                    v-if="!hasAutocomplete"
                    @click="createNewItem(null)">
                    <svg-icon v-if="saving" class="animate-spin w-20 h-20 mr-8" size="small"
                              type="loader"/>
                    <svg-icon v-if="!saving" class="inline max-w-16 max-h-16 text-secondary mr-8" type="add-circle"/>
                    {{ moduleInfo.text.new_item }}
                </button>
            </div>
        </div>

        <module-template-editor
            v-if="editing"
            v-model="editing"
            :service="service"
            @close="editing = null"
        />
    </div>
</template>

<script>
import {isEmpty} from "lodash";
import _debounce from "lodash/debounce";
import {ADD_NOTIFICATION} from '../state/mutation-types';
import {normalizeJson} from '../../shared/helpers';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useNotificationsStore} from '../state/modules/notifications';
import draggable from 'vuedraggable';
// VUE3_MIGRATION - temp fix for vuedraggable, compat issue
draggable.compatConfig = { MODE: 3 }

import SvgIcon from './ui/SvgIcon.vue';
import ClickEditInput from './forms/ClickEditInput.vue';
import InterviewTemplateTitle from './InterviewTemplateTitle.vue';
import ModuleTemplateEditor from "./ui/ModuleTemplateEditor.vue";
import SimpleButton from "./ui/SimpleButton.vue";
import SelectDropdown from "./forms/SelectDropdown.vue";
import PopupModalConfirm from "./ui/PopupModalConfirm.vue";
import CompetenceTemplate from "../models/CompetenceTemplate";
import InterviewTemplate from "../models/InterviewTemplate";
import Competence from "../models/Competence";
import Filter from "../models/Filter";
import Question from "../models/Question";
import FilterGroup from "../models/FilterGroup";

export default {
    components: {
        PopupModalConfirm,
        InterviewTemplateTitle,
        ClickEditInput,
        SvgIcon,
        draggable,
        ModuleTemplateEditor,
        SimpleButton,
        SelectDropdown,
    },
    props: {
        service: null,
        template: null,
    },
    data() {
        return {
            showDeletePopup: false,
            deleting: null,
            saving: false,
            creating: false,
            actionButtons: null,
            duplicating: null,
            currentItem: null,
            drag: false,
            editing: false,
            dirty: false,
            newItem: null,
            newItems: 0,
            optionsLoaded: false,
            newItemButton: ``,
            models: {
                'competences': Competence,
                'filters': Filter,
                'filter-groups': FilterGroup,
                'questions': Question,
                'competence-templates': CompetenceTemplate,
                'interview-templates': InterviewTemplate,
            }
        };
    },
    computed: {
        ...mapState(useMainStore, ['locale', 'currentCompany', 'templateModuleInfo']),
        staticId(){
            let titleArray = this.templateModuleInfo.text.title.split(' ');
            let staticId = '';

            titleArray.forEach((title, index) => {
                // Convert entire word to lowercase first
                title = title.toLowerCase();

                // If it's not the first word (index 0), capitalize the first letter
                if (index !== 0) {
                    title = title.charAt(0).toUpperCase() + title.slice(1);
                }

                staticId += title;
            });

            return staticId
        },
        moduleInfo() {
            return this.templateModuleInfo;
        },
        model() {
            return useRepo(this.models[this.moduleInfo.model]);
        },
        templateModel() {
            return useRepo(this.models[this.moduleInfo.templateModel]);
        },
        countItems() {
            return this.items.length;
        },
        dragOptions() {
            return {
                animation: 200,
                group: 'items',
                disabled: false,
                ghostClass: 'ghost',
            };
        },
        items: {
            get() {
                if (!this.canManage) {
                    return this.service.ungroupedFiltersORM(this.template, this.templateModel, this.moduleInfo.children);
                }

                return this.template[this.moduleInfo.children];
            },
            set(items) {
                this.template[this.moduleInfo.children] = items;
            }
        },
        titleAttribute() {
            return this.moduleInfo.title.attribute;
        },
        hasAutocomplete() {
            return this.moduleInfo.autocomplete || false;
        },
        options() {
            return this.model.where(this.titleAttribute, title => !isEmpty(title)).get()
                .map(item => ({
                    value: item.id,
                    label: this.optionTitle(item)
                }));
        },
        canManage() {
            return Number.isInteger(this.template.id);
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        title(item, title = null) {
            let attribute = this.titleAttribute;
            let locale = this.moduleInfo.title.locale || null;

            if (title) {
                if (locale) {
                    item[attribute][locale] = title;
                } else {
                    item[attribute] = title;
                }
            }

            return (locale ? item[attribute][locale] : item[attribute]) || '';
        },
        getKey(item) {
            return `item-${this.getUniqueId(item)}`;
        },
        getUniqueId(item) {
            return item.pivot ? item.pivot.id : item.id;
        },
        createNewItem(title) {
            this.newItems += 1;

            // TODO
            // title can be a string if a new item is created or
            // it can be an integer if the item already exists

            let attributes = this.initDummyItem(title);
            this.items.push(attributes);

            if (!this.hasAutocomplete) {
                this.$nextTick(() => {
                    this.$refs[`item-new-${this.newItems}`][0].startEdit();
                });
            }
            return attributes;
        },
        initDummyItem(title = null) {
            let locale = this.moduleInfo.title.locale || null;
            let value = locale ? {} : '';
            const order = this.countItems + 1;

            if (!title) {
                let locales = {...this.currentCompany.jobsite.active_languages};

                if (locale) {
                    Object.keys(locales).forEach(index => {
                        value[locales[index]] = '';
                    });
                }
            } else {
                value[locale] = title;
            }

            let dummyItem = {
                id: `new-${this.newItems}`,
                [this.titleAttribute]: value,
            };

            if (this.moduleInfo.hasPivot) {
                dummyItem.pivot = {
                    id: null,
                    order: order
                }
            } else {
                dummyItem.order = order;
            }
            return dummyItem;
        },
        deleteItem(item, confirmed = false) {
            if (this.moduleInfo.deleteItemConfirmation && !confirmed) {
                this.showDeletePopup = true;
                this.deleting = item;
                return;
            }

            const deleteId = this.getUniqueId(item);
            if (!deleteId) {
                this.items = this.items.filter(item => this.getUniqueId(item) !== deleteId);
                return false;
            }

            if (this.moduleInfo.templateModel === 'filter-groups') {
                let filterGroupId = this.template.id;
                this.service.destroyItem(deleteId, filterGroupId).then(() => {
                    this.completeDeletion(deleteId);
                });
            } else {
                this.service.destroyItem(deleteId).then(() => {
                    this.completeDeletion(deleteId);
                });
            }
        },
        completeDeletion(id, hard = false) {
            this[ADD_NOTIFICATION]({
                type: 'success',
                message: this.$t(hard ? `settings.${this.moduleInfo.templateModel}.delete.permanently.success` : `settings.${this.moduleInfo.templateModel}.delete.success`, {
                    type: this.$t(`settings.${this.moduleInfo.model}.single`)
                }),
            });

            if (hard) {
                this.model.destroy(this.deleting.id);
            }

            this.detachItem(this.template.id, id);

            this.items = this.items.filter(item => {
                return this.getUniqueId(item, true) !== id
            });

            this.deleting = false;
        },
        detachItem(templateId, itemId) {
            this.models[this.moduleInfo.templateModel].detach(itemId);
        },
        store(item, attributes, pivot = null) {
            const templateId = Number.isInteger(this.template.id) ? this.template.id : 0;

            return this.service.storeItem(templateId, {
                data: {attributes},
                pivot
            }).then((result) => {
                item.id = result.id;
                this.model.save(normalizeJson(result));
                return result;
            });
        },
        update(item, attributes) {
            return this.service.updateItem(item.id, {
                data: {attributes},
                currentLocaleOnly: true
            }).then(() => {
                this.model
                    .where('id', item.id)
                    .update(attributes);
            });
        },
        changeLinkedItem(item, itemTitle) {
            // TODO: split this code in create / update
            const create = !item;

            if (create) {
                if (!itemTitle) {
                    return false;
                }
                this.creating = true;
                item = this.createNewItem(itemTitle);
            } else {
                this.saving = true;
                this.actionButtons = item;
            }

            const updateKey = typeof itemTitle === 'string' ? this.titleAttribute : this.moduleInfo.pivot_id;

            this.store(item,
                {[updateKey]: itemTitle},
                item.pivot
            ).then(newItem => {
                this.items = this.items.map(currentItem => {
                    const currentPivotKey = Object.keys(currentItem)
                        .find(x => x.startsWith('pivot') && !!currentItem[x]);

                    if (currentPivotKey && currentItem[currentPivotKey].id === item.pivot.id) {
                        currentItem[currentPivotKey] = newItem.pivot;
                        currentItem.id = newItem.id;
                        currentItem[this.titleAttribute] = newItem.attributes[this.titleAttribute];
                    }

                    return currentItem;
                });

                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t(`general.${create ? 'added' : 'edited'}`, {
                        type: this.$t(`settings.${this.moduleInfo.model}.single`)
                    }),
                });

                this.saving = false;
                this.creating = false;
                this.actionButtons = false;
                this.newItem = null;
                this.updateTemplateItems();
            });
        },
        edit(item, newTitle) {
            if (!this.isDirty(item, newTitle)) {
                return;
            }

            this.saving = true;
            this.title(item, newTitle);

            let attributes = {[this.titleAttribute]: newTitle};
            let requests = [];

            if (isNaN(item.id)) {
                requests.push(this.store(item, attributes));
            } else {
                requests.push(this.update(item, attributes));
            }

            Promise.all(requests).then(() => {
                this.saving = false;
            });
        },
        isDirty(item, title) {
            return this.title(item) !== title;
        },
        updateOrder: _debounce(function (evt) {
            const newSortOrder = evt.newDraggableIndex;

            if (newSortOrder === this.currentItem.order) {
                this.currentItem = null;
                this.drag = false;
                return;
            }

            const sortOrders = this.items.filter(item => !isNaN(this.getUniqueId(item))).map((item, index) => {
                return {id: this.getUniqueId(item), order: index};
            });

            this.service.updateItemSortOrders(
                this.template.id, {
                    order: sortOrders
                }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.order_changed', {subject: this.$t(`settings.${this.moduleInfo.model}.title`)}),
                });
            });

            this.currentItem.order = newSortOrder;
            this.drag = false;

            this.items.map((item, index) => {
                if (item.hasOwnProperty('pivot')) {
                    item.pivot.order = index;
                } else {
                    item.order = index;
                }
                return item;
            });

            this.updateTemplateItems();
        }, 750),
        updateTemplateItems() {
            this.templateModel.save({
                'id': this.template.id,
                [this.moduleInfo.children]: this.items
            });
        },
        optionTitle(item) {
            return item[this.moduleInfo.title.attribute][this.moduleInfo.title.locale] || null;
        },
        handleEdit(item) {
            if (this.moduleInfo.title.hasOwnProperty('locale')) {
                this.editing = item;
            } else {
                this.$refs[this.getKey(item)][0].startEdit();
            }
        },
        addItemToUngroupedItems(item) {
            const template = this.templateModel.where((filter) => !Number.isInteger(filter.id)).first();
            if (template && !this.checkIfItemIsUsed(item)) {
                template[this.moduleInfo.children].push(item);

                this.templateModel.save({
                    'id': template.id,
                    [this.moduleInfo.children]: template[this.moduleInfo.children]
                });
            }
        },
        checkIfItemIsUsed(item) {
            return this.templateModel.where(this.moduleInfo.children, items => {
                return items.filter(templateItem => {
                    return templateItem.id === item.id;
                }).length > 0;
            }).get().length > 0;
        },
        confirmPermanentDeletion() {
            const id = this.deleting.id;
            if (this.moduleInfo.templateModel === 'filter-groups') {
                this.service.destroyItemPermanently(id).then(() => {
                    const deleteId = this.getUniqueId(this.deleting);
                    this.completeDeletion(deleteId, true);
                });
            } else {
                this.service.destroyItem(id, null, true).then(() => {
                    const deleteId = this.getUniqueId(this.deleting);
                    this.completeDeletion(deleteId);
                });
            }

            this.showDeletePopup = false;
        },
        async confirmDeletion() {
            await this.deleteItem(this.deleting, true);
            this.showDeletePopup = false;
            this.deleting = null;
        },
    },
    mounted() {
        if (!this.hasAutocomplete) {
            return false;
        }

        this.service.items().then(result => {
            return this.model.save(normalizeJson(result.data));
        }).then(() => {
            this.optionsLoaded = true;
        });
    }
};
</script>
